import {
  BASE_COLUMNS, EVENT_COLUMNS, CLUB_COLUMNS, WEEKDAY_KEYS, artistColumns, TEMPLATE_ARTIST_SLOTS,
} from './markerCsvFormat';
import { ALL_GENRES, ALL_DRESSCODES, ALL_SPECIAL_TYPES, ALL_PRICES } from '../../../constants/EventConstants';

// The rules a CSV must follow to go through the import, written for a language
// model that is asked to produce such a file. The column names and the value
// lists come from the same constants the import, the export and the forms use,
// so the text cannot drift from the code; the prose explains what the backend
// (MarkerReaderCsv, MarkerServiceImpl) and the dashboard's pre-processing
// (BulkCsvUploadModal) actually do with each field.

export const CSV_RULES_FILENAME = 'csv-import-regeln.md';

export const BOX_OFFICE_VALUES = [
  ['LIMITED', 'Abendkasse mit begrenztem Kontingent'],
  ['UNLIMITED', 'Abendkasse ohne Limit'],
  ['PRESALE_ONLY', 'keine Abendkasse, nur Vorverkauf'],
  ['UNKNOWN', 'nicht bekannt (z. B. importierte Events ohne Angabe)'],
];

const DRINK_PRICE_LABELS = { 1: 'Bier unter 4,30 €', 2: 'Bier zwischen 4,30 € und 5,00 €', 3: 'Bier über 5,00 €' };

const DAY_LABELS = {
  monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch', thursday: 'Donnerstag',
  friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag',
};

/** The header the templates and the export use: base, event, three acts, opening hours. */
export const RULES_COLUMNS = [...BASE_COLUMNS, ...EVENT_COLUMNS, ...artistColumns(TEMPLATE_ARTIST_SLOTS), ...CLUB_COLUMNS];

const quote = (value) => {
  const s = String(value ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const EVENT_EXAMPLE = {
  type: 'Event',
  title: 'Neon Pulse Vienna',
  descriptionGerman: 'Techno- und House-Nacht auf zwei Floors mit lokalen DJs.',
  descriptionEnglish: '',
  musicGenre: 'Techno|House',
  image: 'https://www.instagram.com/p/C1a2b3c4d5e/',
  instagramName: 'neonpulse_vienna',
  address: 'Praterstraße 1, 1020 Wien',
  latitude: '48.2154',
  longitude: '16.3928',
  entryPrice: '0',
  minAge: '18',
  dressCode: 'egal',
  drinkPriceIndicator: '2',
  specialType: 'rave',
  eventDate: '2026-11-14',
  startTime: '22:00:00',
  endTime: '06:00:00',
  ticketUrl: '',
  hostCode: '',
  boxOfficePrice: '15',
  boxOfficeAvailability: 'LIMITED',
  hasAwarenessTeam: 'true',
  artist1Name: 'Marika Rossa',
  artist1Instagram: 'marikarossa',
  artist1SpotifyId: '',
  artist2Name: 'DJ Beta',
  artist2Instagram: '',
  artist2SpotifyId: '',
};

const CLUB_EXAMPLE = {
  type: 'Club',
  title: 'Club Aurora',
  descriptionGerman: '',
  descriptionEnglish: 'Modern club focused on house and melodic techno.',
  musicGenre: 'House|Techno',
  image: 'https://www.instagram.com/p/D9z8y7x6w5v/',
  instagramName: 'clubaurora_vienna',
  address: 'Gürtelbogen 37, 1080 Wien',
  latitude: '48.2168',
  longitude: '16.3402',
  entryPrice: '12',
  minAge: '18',
  dressCode: 'elegant',
  drinkPriceIndicator: '3',
  specialType: 'glam',
  thursdayOpen: '20:00:00',
  thursdayClose: '02:00:00',
  fridayOpen: '23:00:00',
  fridayClose: '06:00:00',
  saturdayOpen: '23:00:00',
  saturdayClose: '06:00:00',
};

/** The example file from section 7, also used by the tests to prove the examples import. */
export const exampleCsv = () => [
  RULES_COLUMNS.map(quote).join(','),
  RULES_COLUMNS.map((c) => quote(EVENT_EXAMPLE[c] ?? '')).join(','),
  RULES_COLUMNS.map((c) => quote(CLUB_EXAMPLE[c] ?? '')).join(','),
].join('\n');

const list = (values) => values.map((v) => `\`${v}\``).join(', ');

const columnRows = () => {
  const rows = [
    ['type', 'ja', 'Text', '`Event` oder `Club`'],
    ['title', 'ja', 'Text ≤ 255', 'Name, wie er in der App stehen soll'],
    ['descriptionGerman', 'nein', 'Text ≤ 1000', 'Klartext; eine Sprache reicht, die andere wird übersetzt'],
    ['descriptionEnglish', 'nein', 'Text ≤ 1000', 'siehe descriptionGerman'],
    ['musicGenre', 'nein', 'Liste, Trenner `\\|`', list(ALL_GENRES)],
    ['image', 'nein', 'URL ≤ 255', 'Instagram-Post-URL `https://www.instagram.com/p/<code>/`'],
    ['instagramName', 'nein (empfohlen)', 'Text', 'Instagram-Handle des Veranstalters ohne `@`'],
    ['address', 'nein', 'Text ≤ 255', '`Straße Nr, PLZ Ort`, in Anführungszeichen'],
    ['latitude', 'ja', 'Dezimalzahl', 'Breitengrad WGS84, z. B. `48.2082`'],
    ['longitude', 'ja', 'Dezimalzahl', 'Längengrad WGS84, z. B. `16.3738`'],
    ['entryPrice', 'nein', 'Zahl in Euro', 'Event: Vorverkaufspreis, `0` = kein Vorverkauf; Club: regulärer Eintritt'],
    ['minAge', 'nein', 'Ganzzahl', 'z. B. `16`, `18`, `21`'],
    ['dressCode', 'nein', 'Text', list(ALL_DRESSCODES.map((d) => d.value))],
    ['drinkPriceIndicator', 'nein', 'Ganzzahl', list(ALL_PRICES)],
    ['specialType', 'nein (empfohlen)', 'Text', list(ALL_SPECIAL_TYPES.map((t) => t.value))],
    ['eventDate', 'Event: ja', 'Datum', '`YYYY-MM-DD`'],
    ['startTime', 'Event: ja', 'Uhrzeit', '`HH:MM:SS`, 24 h'],
    ['endTime', 'Event: ja', 'Uhrzeit', '`HH:MM:SS`; kleiner als startTime = Folgetag'],
    ['ticketUrl', 'nein', 'URL', 'vollständig mit `https://`; nur bei Online-Vorverkauf'],
    ['hostCode', 'nein', 'Text', 'normalerweise leer, wird aus instagramName abgeleitet'],
    ['boxOfficePrice', 'nein', 'Zahl in Euro', 'Pflicht bei `LIMITED` und `UNLIMITED`'],
    ['boxOfficeAvailability', 'nein', 'Text', list(BOX_OFFICE_VALUES.map(([v]) => v))],
    ['hasAwarenessTeam', 'nein', 'Boolean', '`true` oder `false`'],
    [`artist1Name … artist${TEMPLATE_ARTIST_SLOTS}SpotifyId`, 'nein', 'Text', 'pro Act drei Spalten: Name, Instagram-Handle, Spotify-Artist-ID'],
    ['mondayOpen … sundayClose', 'Club: mind. ein Tag', 'Uhrzeit', '`HH:MM:SS`; Öffnung und Schließung immer zusammen'],
  ];
  return rows.map(([c, req, type, values]) => `| \`${c}\` | ${req} | ${type} | ${values} |`).join('\n');
};

const detailBlocks = () => {
  const dressCodes = ALL_DRESSCODES.map((d) => `\`${d.value}\` (${d.label})`).join(', ');
  const specialTypes = ALL_SPECIAL_TYPES.map((t) => `\`${t.value}\` (${t.label})`).join(', ');
  const boxOffice = BOX_OFFICE_VALUES.map(([v, label]) => `\`${v}\` = ${label}`).join('; ');
  const drinkPrices = ALL_PRICES.map((p) => `\`${p}\` = ${DRINK_PRICE_LABELS[p]}`).join(', ');
  const days = WEEKDAY_KEYS.map((d) => `\`${d}Open\`/\`${d}Close\` (${DAY_LABELS[d]})`).join(', ');
  const artistSlots = artistColumns(TEMPLATE_ARTIST_SLOTS).map((c) => `\`${c}\``).join(', ');

  return `### type
- Pflicht. Genau \`Event\` oder \`Club\` (so geschrieben). Entscheidet, welche weiteren Spalten Pflicht sind (Abschnitt 2).

### title
- Pflicht. Der Name, wie er in der App erscheinen soll, maximal 255 Zeichen. Kein Datum, keine Uhrzeit, kein Ort im Titel, das steht in eigenen Spalten.

### descriptionGerman, descriptionEnglish
- Optional. Klartext ohne Markdown oder HTML, maximal 1000 Zeichen (längere Texte schneidet der Import ab).
- Es reicht eine Sprache: ist nur eine ausgefüllt, übersetzt das Backend beim Import automatisch in die andere. Beide leer ist erlaubt.

### musicGenre
- Optional. Ein oder mehrere Werte, getrennt mit \`|\` (senkrechter Strich), zum Beispiel \`Techno|House\`.
- Erlaubte Werte, genau so geschrieben: ${list(ALL_GENRES)}.
- Andere Werte werden zwar gespeichert, sind in der App aber nicht filterbar und haben keine Farbe. Deshalb nur Werte aus der Liste verwenden und ein fremdes Genre auf das nächstliegende abbilden (z. B. "Drum and Bass" → \`DnB\`, "Hip-Hop" → \`HipHop\`, "Klassik" → \`Classical\`).

### image
- Optional. Die URL eines Instagram-Posts des Veranstalters in der Form \`https://www.instagram.com/p/<code>/\` (auch \`/reel/\` geht). Die App lädt daraus das Titelbild.
- Alternativ eine direkte Bild-URL (\`https://…jpg\`). Keine Base64-Daten, maximal 255 Zeichen.

### instagramName
- Optional, aber empfohlen: der Instagram-Handle des Veranstalters bzw. Clubs ohne \`@\`, zum Beispiel \`grelleforelle\`.
- Bei Events ordnet der Import das Event über diesen Handle dem Host-Code des Accounts zu (siehe hostCode).

### address
- Optional. \`Straße Hausnummer, PLZ Ort\`, zum Beispiel \`Praterstraße 1, 1020 Wien\`. Wegen des Kommas in Anführungszeichen setzen.

### latitude, longitude
- Pflicht. Dezimalgrad nach WGS84 mit Punkt als Dezimaltrenner (\`48.2082\`, \`16.3738\`); ein Komma wird akzeptiert. Keine Grad/Minuten, kein "N"/"E".
- Wien liegt ungefähr bei latitude 48.11 bis 48.33 und longitude 16.18 bis 16.58. Werte außerhalb von -90..90 bzw. -180..180 lehnt der Import ab.
- Die Koordinaten müssen zur Adresse passen; sie bestimmen die Position auf der Karte.

### entryPrice
- Optional. Zahl in Euro ohne Währungszeichen, Punkt als Dezimaltrenner (\`12\` oder \`12.5\`; Komma wird akzeptiert).
- Bei Events ist das der **Vorverkaufspreis**. \`0\` bedeutet "kein Vorverkauf" (die App zeigt dann keinen Vorverkaufspreis, die Website "Gratis"). Leer bedeutet "keine Angabe".
- **Freier Eintritt** ("Eintritt frei", "free entry", "gratis"): \`entryPrice\` \`0\`, \`boxOfficeAvailability\` \`UNLIMITED\`, \`boxOfficePrice\` \`0\`. Nur so zeigt die App "Gratis"; mit \`UNKNOWN\` steht dort "Nicht bekannt".
- Bei Clubs ist das der reguläre Eintritt.

### minAge
- Optional. Ganzzahl, Mindestalter in Jahren, zum Beispiel \`16\`, \`18\`, \`21\`.

### dressCode
- Optional. Genau einer von: ${dressCodes}. Klein geschrieben.
- Unbekannte Werte macht der Import zu \`egal\`; Synonyme wie \`casual\`, \`formal\`, \`sexy\`, \`sport\`, \`office\`, \`verkleidung\` werden abgebildet. Trotzdem die Werte der Liste schreiben.

### drinkPriceIndicator
- Optional. ${drinkPrices}. Referenz ist der Preis für ein Bier.

### specialType
- Optional, bei Events praktisch immer angeben. Genau einer von: ${specialTypes}. Klein geschrieben, keine Leerzeichen.
- Bestimmt Icon und Filter in der App; ohne Wert erscheint das Event unter "Allgemein". Zuordnung: Konzert/Live-Musik → \`concert\`; Clubnacht, DJ-Line-up, Techno/House → \`rave\`; After-Work-Party → \`afterwork\`; Studentenparty → \`uni\`; Karaoke → \`karaoke\`; Pub Quiz → \`pubquiz\`; Markt, Festival, Straßenfest → \`market\`; Ausstellung, Theater, Lesung → \`art\`; Essen und Trinken → \`food\`; Familien- oder Kinderprogramm → \`family\`; Sportevent → \`sport\`; Ball, Gala, Dresscode elegant → \`glam\`; queeres Event → \`pride\`; sonst \`other\`.

### eventDate (nur Event)
- Pflicht bei Events. \`YYYY-MM-DD\`, zum Beispiel \`2026-11-14\`. Der Tag, an dem das Event **beginnt**, auch wenn es nach Mitternacht endet.
- Vergangene Events werden importiert, aber in der App nicht mehr angezeigt.

### startTime, endTime (nur Event)
- Pflicht bei Events. \`HH:MM:SS\` im 24-Stunden-Format (\`22:00:00\`); \`HH:MM\` wird um \`:00\` ergänzt.
- Ist endTime kleiner oder gleich startTime, endet das Event am Folgetag: \`22:00:00\` bis \`06:00:00\` ist eine Nacht bis 6 Uhr früh.

### ticketUrl (nur Event)
- Optional. Vollständige URL mit \`https://\` zur Ticketseite. Nur ausfüllen, wenn es Online-Vorverkauf gibt; dann sollte entryPrice größer als 0 sein.

### hostCode (nur Event)
- Optional, normalerweise leer lassen. Der Code, unter dem ein Veranstalter seine Events verwaltet. Ist er leer und instagramName gesetzt, ordnet der Import das Event dem Host-Code dieses Instagram-Accounts zu und legt den Code bei Bedarf an (die Import-Antwort listet neue Codes auf).

### boxOfficePrice (nur Event)
- Optional. Preis an der Abendkasse in Euro als Zahl. Pflicht, wenn boxOfficeAvailability \`LIMITED\` oder \`UNLIMITED\` ist. Bei \`PRESALE_ONLY\` \`0\` oder leer, bei \`UNKNOWN\` leer.

### boxOfficeAvailability (nur Event)
- Optional. Genau einer von: ${boxOffice}. In Großbuchstaben.

### hasAwarenessTeam (nur Event)
- Optional. \`true\` oder \`false\` (auch \`1\`/\`0\`, \`ja\`/\`nein\`, \`wahr\`/\`falsch\` werden verstanden). Leer bedeutet "unbekannt". Nur \`true\` schreiben, wenn ein Awareness-Team ausdrücklich angekündigt ist.

### Line-up: ${artistSlots} (nur Event)
- Optional. Pro Act drei Spalten; die Vorlage hat Platz für ${TEMPLATE_ARTIST_SLOTS} Acts, weitere Dreiergruppen \`artist${TEMPLATE_ARTIST_SLOTS + 1}Name\`, \`artist${TEMPLATE_ARTIST_SLOTS + 1}Instagram\`, \`artist${TEMPLATE_ARTIST_SLOTS + 1}SpotifyId\` usw. sind erlaubt.
- \`artistNName\`: der Künstlername, so wie der Act sich schreibt. \`artistNInstagram\`: Instagram-Handle ohne \`@\` (optional). \`artistNSpotifyId\`: die 22-stellige Spotify-Artist-ID, zum Beispiel \`1WNoKxsp715jez1Td4vthc\` (optional).
- Die Spotify-ID nur eintragen, wenn sie sicher bekannt ist. Acts ohne ID sucht das Backend nach dem Import selbst auf Spotify und verknüpft nur exakte Namenstreffer; der Rest wird im Dashboard über den Spotify-Abgleich geprüft. Nie eine ID raten.
- Ein Act mit Handle, aber ohne Namen wird unter dem Handle gespeichert. Die Zeichen \`|\` und \`;\` dürfen in Name und Handle nicht vorkommen.
- Acts sind im Backend eindeutig: derselbe Name oder Handle in mehreren Zeilen bezeichnet denselben Act.
- Alternativ zu den Dreiergruppen kann eine Spalte \`artists\` im Backend-Format stehen: \`Name;instagram=handle;spotifyId=id|Name2\`. Dann werden die artistN-Spalten ignoriert.

### Öffnungszeiten: ${days} (nur Club)
- Bei Clubs Pflicht: mindestens ein Tag mit Öffnung **und** Schließung. Tage, an denen der Club geschlossen ist, bleiben komplett leer. Bei Events bleiben alle 14 Spalten leer.
- Format \`HH:MM:SS\`. Eine Schließung vor der Öffnung bedeutet nach Mitternacht: \`fridayOpen=23:00:00\`, \`fridayClose=06:00:00\` ist Freitag 23 Uhr bis Samstag 6 Uhr früh und gehört zum Freitag.
- Alternativ eine Spalte \`weekdays\` im Backend-Format: \`Friday@23:00:00-06:00:00|Saturday@23:00:00-06:00:00\` (englische Tagesnamen).

### markerType
- Nicht anlegen. Wird aus type abgeleitet.

### id
- Nur im Export des Dashboards enthalten. Eine Zeile mit der id eines bestehenden Markers aktualisiert diesen Marker (Re-Import eines korrigierten Exports); eine leere id legt einen neuen an.
- Eine id, die es nicht gibt, oder eine Club-id in einer Event-Zeile (und umgekehrt) wird als Fehler dieser Zeile gemeldet. Für neue Einträge die Spalte leer lassen, nie eine id erfinden.`;
};

/** The complete rules document as Markdown. */
export function buildCsvRules(today = new Date()) {
  const stand = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const header = RULES_COLUMNS.map((c) => `\`${c}\``).join(', ');

  return `# CSV-Import: Regeln für Events und Clubs

Stand ${stand}, erzeugt vom Admin-Dashboard. Dieses Dokument beschreibt vollständig, wie eine CSV-Datei aussehen muss, damit der CSV-Import des Dashboards sie annimmt. Es ist für ein Sprachmodell geschrieben, das aus Rohdaten (Instagram-Posts, Websites, Notizen) eine solche Datei erzeugen soll.

## 1. Datei

- Kodierung UTF-8, optional mit Byte Order Mark. Trennzeichen ist das Komma; ein Semikolon wird vom Dashboard-Import ebenfalls erkannt, ist aber zweite Wahl. Zeilenumbruch \`\\n\` oder \`\\r\\n\`.
- Die erste Zeile enthält die technischen Spaltennamen aus Abschnitt 3, genau so geschrieben (Groß- und Kleinschreibung zählt). Danach eine Zeile pro Event oder Club. Keine Beschreibungs- oder Überschriftenzeilen dazwischen, keine Leerzeilen.
- Die Reihenfolge der Spalten ist egal. Optionale Spalten dürfen fehlen; unbekannte Spalten werden ignoriert.
- Werte, die ein Komma, ein Anführungszeichen oder einen Zeilenumbruch enthalten, in doppelte Anführungszeichen setzen; ein Anführungszeichen im Wert wird verdoppelt (\`""\`).
- Eine leere Zelle bedeutet "keine Angabe". Niemals \`null\`, \`-\`, \`n/a\`, \`unbekannt\` oder Ähnliches schreiben.
- Zeilen, deren \`type\` weder \`Event\` noch \`Club\` ist, überspringt der Import stillschweigend. Ein Fehler in einer Zeile betrifft nur diese Zeile, die übrigen werden importiert.
- Zeilen ohne id legen neue Einträge an. Der Import erkennt keine Duplikate: ein zweimal ohne id importiertes Event ist zweimal in der App. Korrekturen deshalb über den Export mit id einspielen.
- Importierte Events landen unter "Pending Events" und müssen freigegeben werden. Importierte Clubs sind sofort in der App.

## 2. Zeilentypen

- \`type=Event\`: zusätzlich Pflicht sind \`eventDate\`, \`startTime\`, \`endTime\`. Die 14 Spalten für Öffnungszeiten bleiben leer.
- \`type=Club\`: zusätzlich Pflicht ist mindestens ein Wochentag mit Öffnung und Schließung. Die Event-Spalten (\`eventDate\`, \`startTime\`, \`endTime\`, \`ticketUrl\`, \`hostCode\`, \`boxOfficePrice\`, \`boxOfficeAvailability\`, \`hasAwarenessTeam\`, alle \`artistN…\`) bleiben leer.
- Für beide Typen Pflicht: \`type\`, \`title\`, \`latitude\`, \`longitude\`.
- Beide Typen dürfen in einer Datei gemischt vorkommen.

## 3. Spalten im Überblick

Vollständige Kopfzeile der Vorlage: ${header}.

| Spalte | Pflicht | Typ | Werte / Format |
|---|---|---|---|
${columnRows()}

## 4. Spalten im Detail

${detailBlocks()}

## 5. Bedingte Regeln (Vorverkauf, Tickets, Abendkasse)

Diese Kombinationen sind gültig; andere vermeiden.

- **Kein Vorverkauf, Abendkasse mit begrenztem Kontingent:** \`entryPrice=0\`, \`ticketUrl\` leer, \`boxOfficeAvailability=LIMITED\`, \`boxOfficePrice=<Preis an der Abendkasse>\`.
- **Kein Vorverkauf, Abendkasse ohne Limit:** \`entryPrice=0\`, \`ticketUrl\` leer, \`boxOfficeAvailability=UNLIMITED\`, \`boxOfficePrice=<Preis>\`.
- **Vorverkauf online und Abendkasse:** \`entryPrice=<Vorverkaufspreis>\`, \`ticketUrl=<Ticketseite>\`, \`boxOfficeAvailability=LIMITED\` oder \`UNLIMITED\`, \`boxOfficePrice=<Preis an der Abendkasse>\`.
- **Nur Vorverkauf, keine Abendkasse:** \`entryPrice=<Vorverkaufspreis>\`, \`ticketUrl=<Ticketseite>\`, \`boxOfficeAvailability=PRESALE_ONLY\`, \`boxOfficePrice\` leer oder \`0\`.
- **Freier Eintritt:** \`entryPrice=0\`, \`ticketUrl\` leer, \`boxOfficeAvailability=UNLIMITED\`, \`boxOfficePrice=0\`.
- **Nichts über Tickets bekannt:** \`entryPrice\` leer, \`ticketUrl\` leer, \`boxOfficeAvailability=UNKNOWN\`, \`boxOfficePrice\` leer.
- \`ticketUrl\` nie ohne Vorverkauf (also nie bei \`entryPrice=0\` oder leer). \`boxOfficePrice\` nie ohne \`LIMITED\` oder \`UNLIMITED\`. Preise nie mit Währungszeichen, Bereiche ("10–15 €") nicht abbilden, sondern den niedrigsten Preis nehmen.
- Ein Event, das über Mitternacht geht: \`eventDate\` ist der Starttag, \`endTime\` die Endzeit am Folgetag (kleiner als \`startTime\`).
- Ein mehrtägiges Event ist eine Zeile pro Tag mit gleichem Titel.
- Bei Events genau einen \`specialType\` wählen (Abschnitt 4), bei Unsicherheit \`other\`.
- Beschreibung: nur eine Sprache ausfüllen, wenn keine Übersetzung vorliegt.

## 6. Was der Dashboard-Import selbst korrigiert

Diese Abweichungen werden beim Hochladen im Dashboard repariert. Trotzdem gilt: die kanonische Form aus Abschnitt 3 und 4 schreiben, weil der Backend-Import ohne Dashboard sie nicht kennt.

- Dezimalkomma statt Punkt in Zahlen (\`48,2082\`, \`12,5\`).
- \`wahr\`/\`falsch\`, \`ja\`/\`nein\`, \`1\`/\`0\` für Boolean-Felder.
- Uhrzeiten \`HH:MM\` werden zu \`HH:MM:SS\`; ein Datum \`TT.MM.JJJJ\` wird zu \`JJJJ-MM-TT\`.
- Ein führendes \`@\` im Instagram-Handle wird entfernt.
- Genres mit Komma statt \`|\`.
- Synonyme für dressCode werden auf die erlaubten Werte abgebildet, Unbekanntes wird \`egal\`.
- Beschreibungen über 1000 Zeichen werden abgeschnitten.
- Semikolon als Trennzeichen und Windows-1252-Kodierung werden erkannt; die Beschreibungszeile der xlsx-Vorlage wird übersprungen.

## 7. Beispiel

Eine gültige Datei mit einem Event (kein Vorverkauf, Abendkasse begrenzt, Nacht bis 6 Uhr, zwei Acts) und einem Club (Donnerstag bis Samstag geöffnet):

\`\`\`csv
${exampleCsv()}
\`\`\`
`;
}
